<template>
  <div class="space-y-4">
    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center py-8">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4">
      <p class="text-red-600">{{ error }}</p>
      <button @click="$emit('retry')" class="mt-2 text-sm text-red-600 hover:text-red-800">
        Try again
      </button>
    </div>

    <!-- Empty State -->
    <div v-else-if="members.length === 0" class="text-center py-8 text-gray-500">
      <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
      <p class="mt-2">No members in this group yet</p>
    </div>

    <!-- Members List -->
    <div v-else class="divide-y divide-gray-100">
      <div
        v-for="member in members"
        :key="member.user_id"
        class="py-3 px-2 flex items-center justify-between gap-4 hover:bg-gray-50 rounded-lg transition-colors"
      >
        <!-- Avatar + info -->
        <div class="flex items-center gap-3 min-w-0">
          <!-- Avatar -->
          <div class="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
            <span class="text-primary-700 font-semibold text-sm">
              {{ initials(member) }}
            </span>
          </div>

          <!-- Name / email / joined -->
          <div class="min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <p class="text-sm font-medium text-gray-900 truncate">
                {{ displayName(member) }}
              </p>
              <!-- Badges -->
              <span
                v-if="isOwner(member)"
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700"
              >
                Owner
              </span>
              <span
                v-else
                :class="roleBadgeClass(member.role)"
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
              >
                {{ member.role }}
              </span>
              <span
                v-if="isCurrentUser(member)"
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700"
              >
                You
              </span>
            </div>
            <p class="text-xs text-gray-400 mt-0.5">
              {{ member.email }}
              <span class="mx-1">·</span>
              Joined {{ formatJoined(member.joined_at) }}
            </p>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex items-center gap-2 flex-shrink-0">
          <template v-if="!isOwner(member) && !isCurrentUser(member)">
            <!-- Role selector — visible to admins and the owner -->
            <template v-if="canManage">
              <select
                v-if="!isChangingRole[member.user_id]"
                :value="member.role"
                @change="handleRoleChange(member, $event)"
                class="text-xs border border-gray-300 rounded-md px-2 py-1 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
              <div v-else class="flex items-center gap-1 text-xs text-gray-500">
                <div class="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-primary-500"></div>
                Updating…
              </div>
            </template>

            <!-- Remove — owner only (DELETE /v1/groups/:id/members/:user_id) -->
            <button
              v-if="isCurrentUserOwner"
              @click="handleRemoveMember(member)"
              :disabled="!!isRemoving[member.user_id]"
              title="Remove member"
              class="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-40"
            >
              <svg v-if="!isRemoving[member.user_id]" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <div v-else class="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
            </button>
          </template>

          <!-- Leave Group — current user, non-owner -->
          <button
            v-else-if="isCurrentUser(member) && !isOwner(member)"
            @click="handleLeaveGroup"
            :disabled="isLeaving"
            class="text-xs text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded-md transition-colors disabled:opacity-40"
          >
            {{ isLeaving ? 'Leaving…' : 'Leave Group' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Remove confirmation -->
    <div v-if="memberToRemove"
      class="fixed inset-0 bg-gray-500/75 flex items-center justify-center z-50">
      <div class="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
        <h3 class="text-base font-semibold text-gray-900 mb-2">Remove Member</h3>
        <p class="text-sm text-gray-500 mb-5">
          Are you sure you want to remove
          <strong>{{ displayName(memberToRemove) }}</strong> from this group?
          This action cannot be undone.
        </p>
        <div class="flex justify-end gap-3">
          <button @click="memberToRemove = null"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button @click="confirmRemoveMember"
            :disabled="!!isRemoving[memberToRemove.user_id]"
            class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50">
            {{ isRemoving[memberToRemove.user_id] ? 'Removing…' : 'Remove' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Leave confirmation -->
    <div v-if="showLeaveConfirm"
      class="fixed inset-0 bg-gray-500/75 flex items-center justify-center z-50">
      <div class="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
        <h3 class="text-base font-semibold text-gray-900 mb-2">Leave Group</h3>
        <p class="text-sm text-gray-500 mb-5">
          Are you sure you want to leave this group? You will need to be re-invited to rejoin.
        </p>
        <div class="flex justify-end gap-3">
          <button @click="showLeaveConfirm = false"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button @click="confirmLeaveGroup"
            :disabled="isLeaving"
            class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50">
            {{ isLeaving ? 'Leaving…' : 'Leave Group' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useAuthStore } from '../../stores/auth'
import { memberDisplayName, type GroupMember } from '../../types/group'

interface Props {
  members: GroupMember[]
  canManage: boolean
  groupId: string
  ownerId?: number        // group.owner_id — used to show Owner badge and protect them
  loading?: boolean
  error?: string | null
}

const props = defineProps<Props>()

const emit = defineEmits<{
  removeMember: [member: GroupMember]
  updateRole: [member: GroupMember, newRole: string]
  leaveGroup: []
  retry: []
}>()

const authStore = useAuthStore()
const currentUserId = computed(() => authStore.user?.id)

const memberToRemove = ref<GroupMember | null>(null)
const showLeaveConfirm = ref(false)
const isRemoving = ref<Record<number, boolean>>({})
const isChangingRole = ref<Record<number, boolean>>({})
const isLeaving = ref(false)

// Clear loading states for members that no longer appear in the list
// (emit is not awaitable in Vue 3, so we can't rely on try/finally after emit)
watch(() => props.members, (newMembers) => {
  const ids = new Set(newMembers.map(m => m.user_id))
  for (const id of Object.keys(isRemoving.value).map(Number)) {
    if (!ids.has(id)) delete isRemoving.value[id]
  }
}, { deep: true })

// ── Helpers ───────────────────────────────────────────────────────────────────
function displayName(m: GroupMember) { return memberDisplayName(m) }

function initials(m: GroupMember): string {
  const name = displayName(m)
  const parts = name.trim().split(' ')
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  return name.substring(0, 2).toUpperCase()
}

function isOwner(m: GroupMember) {
  // Owner is identified by owner_id at the group level, not by role
  return props.ownerId != null && m.user_id === props.ownerId
}

function isCurrentUser(m: GroupMember) {
  return m.user_id === currentUserId.value
}

const isCurrentUserOwner = computed(() =>
  props.ownerId != null && authStore.user?.id === props.ownerId
)

function roleBadgeClass(role: string) {
  return role === 'admin'
    ? 'bg-blue-100 text-blue-700'
    : 'bg-gray-100 text-gray-600'
}

function formatJoined(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

// ── Actions ───────────────────────────────────────────────────────────────────
async function handleRoleChange(member: GroupMember, event: Event) {
  const newRole = (event.target as HTMLSelectElement).value
  if (newRole === member.role) return
  isChangingRole.value[member.user_id] = true
  try { await emit('updateRole', member, newRole) }
  finally { isChangingRole.value[member.user_id] = false }
}

function handleRemoveMember(member: GroupMember) {
  memberToRemove.value = member
}

async function confirmRemoveMember() {
  if (!memberToRemove.value) return
  isRemoving.value[memberToRemove.value.user_id] = true
  try {
    await emit('removeMember', memberToRemove.value)
    memberToRemove.value = null
  } finally {
    if (memberToRemove.value) isRemoving.value[memberToRemove.value.user_id] = false
  }
}

function handleLeaveGroup() { showLeaveConfirm.value = true }

async function confirmLeaveGroup() {
  isLeaving.value = true
  try {
    await emit('leaveGroup')
    showLeaveConfirm.value = false
  } finally {
    isLeaving.value = false
  }
}
</script>
